<template>
  <form v-if="ready" @submit.prevent="handleSubmit" class="space-y-8 divide-y divide-gray-200">
    <div class="space-y-6">
      <div class="grid grid-cols-1 gap-y-6 gap-x-4 lg:grid-cols-12">
        <div class="lg:col-span-5">
          <label for="client" class="block text-lg font-medium text-gray-700 mb-1"> Client </label>
          <div class="mt-1 flex rounded-md">
            <p
              v-if="typeof formValues.client?.name !== 'undefined'"
              class="border-1 w-full px-2 py-1 mt-1 border-gray-200 shadow rounded bg-gray-200 font-bold cursor-pointer"
              @click="formValues.client = null"
            >
              <span>{{ formValues.client.name }}</span>
              <span v-if="formValues.client.vat"> ({{ formValues.client.vat }})</span>
            </p>
            <UISelectClient v-else v-model="formValues.client" />
          </div>
        </div>
      </div>

      <label for="product-1" class="block text-lg font-medium text-gray-700 mb-1">
        {{ creditNote ? 'Remboursements' : 'Produits' }}
      </label>
      <div
        v-for="(p, index) in formValues.products"
        :key="`product-${p.code}-${index}-${formValues.products.length}`"
        class="grid grid-cols-1 gap-x-4 lg:grid-cols-12"
        :style="index == 0 ? 'margin-top: 2px' : ''"
      >
        <div class="lg:col-span-5 bg-white border-1 border-gray-500 p-4 rounded relative">
          <i
            v-if="formValues.products.length > 1"
            @click.prevent.stop="removeProduct(index)"
            class="fas fa-times bg-red-500 text-white font-bold cursor-pointer w-6 h-6 -mt-3 -mr-3 text-center flex justify-center items-center rounded-full top-0 right-0 absolute"
          ></i>
          <div class="rounded-md">
            <div class="grid xl:grid-cols-4 gap-x-2">
              <div class="col-span-3">
                <label for="phone" class="block text-sm font-medium text-gray-700">
                  {{ creditNote ? 'Remboursement' : 'Produit' }} #{{ index + 1 }}
                </label>
                <template v-if="!creditNote">
                  <p
                    v-if="p.code"
                    class="border-1 w-full px-2 py-1 mt-1 border-gray-200 shadow rounded bg-gray-200 font-bold cursor-pointer"
                    @click="formValues.products[index] = Object.assign({}, getEmptyProduct(p))"
                  >
                    {{ p.code }} <span v-if="p.description">({{ p.description }})</span>
                  </p>
                  <UISelectProduct
                    v-else
                    class="block"
                    :value="formValues.products[index]"
                    @selected="selectedProduct($event, index)"
                  />
                </template>
              </div>
              <div v-if="!creditNote">
                <label :for="`quantity-${index}`" class="block text-sm font-medium text-gray-700"> Quantité </label>
                <input
                  v-model="p.quantity"
                  type="text"
                  :name="`quantity-${index}`"
                  :id="`quantity-${index}`"
                  :disabled="loading || typeof p.id === 'undefined'"
                  class="block w-full sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md border-gray-200"
                />
              </div>
            </div>
          </div>
          <div class="rounded-md mt-1">
            <div class="grid xl:grid-cols-9 gap-x-2">
              <div class="col-span-2" v-if="!creditNote && p.id && p.price_type === 'PER_SQUARE_METER'">
                <label :for="`largeur-${index}`" class="block text-sm font-medium text-gray-700"> Largeur </label>
                <input
                  type="text"
                  v-model="p.width"
                  @blur="p.width = String(p.width).replace(',', '.')"
                  :name="`largeur-${index}`"
                  :id="`largeur-${index}`"
                  :disabled="loading || typeof p.id === 'undefined'"
                  class="block w-full sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md border-gray-200"
                />
                <small class="block text-xs pl-2 text-gray-400">En metres</small>
              </div>
              <div class="col-span-2" v-if="!creditNote && p.id && p.price_type !== 'FIXED'">
                <label :for="`longeur-${index}`" class="block text-sm font-medium text-gray-700"> Longeur </label>
                <input
                  type="text"
                  v-model="p.length"
                  @blur="p.length = String(p.length).replace(',', '.')"
                  :name="`longeur-${index}`"
                  :id="`longeur-${index}`"
                  :disabled="loading || typeof p.id === 'undefined'"
                  class="block w-full sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md border-gray-200"
                />
                <small class="block text-xs pl-2 text-gray-400">En metres</small>
              </div>
              <div class="col-span-3">
                <label :for="`price-${index}`" class="block text-sm font-medium text-gray-700"> Prix (HTVA) </label>
                <input
                  v-model="p.price"
                  type="text"
                  :name="`price-${index}`"
                  :id="`price-${index}`"
                  :disabled="loading || typeof p.id === 'undefined'"
                  class="block w-full sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md border-gray-200"
                />
              </div>
              <div class="col-span-2">
                <label :for="`vat-${index}`" class="block text-sm font-medium text-gray-700"> TVA </label>
                <select
                  v-model="p.vat"
                  :name="`vat-${index}`"
                  :id="`vat-${index}`"
                  :disabled="loading || typeof p.id === 'undefined'"
                  class="p-2 block w-full sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md border-gray-200"
                >
                  <option value="0">0%</option>
                  <option value="6">6%</option>
                  <option value="12">12%</option>
                  <option value="21">21%</option>
                </select>
              </div>
              <div class="col-span-4 pt-1 text-center" v-show="creditNote && p.price && p.vat">
                <label :for="`vat-${index}`" class="block text-sm font-medium text-gray-700">
                  Remboursement TVAC
                </label>
                <p class="text-center w-full block">&euro;&nbsp;{{ ((1 + p.vat / 100) * p.price).toFixed(2) }}</p>
              </div>
            </div>
          </div>
          <div class="rounded-md mt-1">
            <div class="grid gap-x-6">
              <div>
                <label :for="`description-${index}`" class="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  v-model="p.description"
                  type="text"
                  rows="4"
                  :name="`description-${index}`"
                  :id="`description-${index}`"
                  :disabled="loading || typeof p.id === 'undefined'"
                  class="block w-full sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md border-gray-200"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button type="button" class="-mt-2" @click.prevent="addProduct">
        + Ajouter un {{ creditNote ? 'remboursement' : 'produit' }}
      </button>

      <div class="rounded-md mt-1">
        <div class="grid xl:grid-cols-5 gap-x-6">
          <div class="col-span-3">
            <label for="description" class="block text-lg font-medium text-gray-700 mb-1"> Description </label>
            <textarea
              v-model="formValues.description"
              type="text"
              rows="4"
              name="description"
              id="description"
              :disabled="loading"
              class="block w-full sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md border-gray-200"
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <div class="pt-5">
      <div class="flex">
        <button
          :disabled="loading || !(postData.client_id && postData.products.length)"
          type="submit"
          :class="{ 'hover:bg-indigo-700': !(loading || !(postData.client_id && postData.products.length)) }"
          class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <i v-if="loading" class="fas fa-spinner mt-1"></i>
          <span v-else>{{ edit_doc_number ? 'Modifier' : 'Creer' }} {{ String(type || '').toLowerCase() }}</span>
        </button>
      </div>
    </div>
  </form>

  <LayoutDocument
    style="max-height: 80vh"
    :key="JSON.stringify(mockDocument) + mockDocument.date"
    hide-print
    v-if="mockDocument.client?.name && mockDocument.products.length"
  >
    <DocumentView
      :key="renderKey"
      :type="type"
      :api-type="apiType"
      :mock-document="mockDocument"
      :mock-products="mockDocument.products"
      :mock-client="mockDocument.client"
    />
  </LayoutDocument>
  <p v-else class="w-full text-red-400 font-bold text-center py-8">
    Veuiller complete le formulaire pour voir un example du document.
  </p>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import clone from 'just-clone'
import useApi from '@/hooks/useApi'
import UISelectClient from '@/components/UI/Molecule/SelectClient'
import UISelectProduct from '@/components/UI/Molecule/SelectProduct'
import DocumentView from '@/components/Templates/DocumentView'
import LayoutDocument from '@/layouts/Document'

export default {
  props: {
    type: {
      type: String,
      required: true,
      default: 'Facture',
    },
    apiType: {
      type: String,
      required: true,
    },
    edit_doc_number: [String, Number],
    creditNote: Boolean,
  },
  components: {
    UISelectClient,
    UISelectProduct,
    DocumentView,
    LayoutDocument,
  },
  setup(props) {
    const router = useRouter()
    const formValues = ref({
      client: ref(null),
      products: ref([]),
      description: ref(''),
      rules: ref([]),
    })

    const getEmptyProduct = (oldProduct = null) => {
      if (!oldProduct) return { vat: 21 }
      return {
        vat: 21,
        length: oldProduct.length || null,
        width: oldProduct.width || null,
        quantity: Math.max(oldProduct.quantity || 0, 1),
      }
    }
    const getNewProduct = () => {
      if (props.creditNote) {
        return { id: null, quantity: 1, vat: 21, description: 'Remboursement' }
      }
      const l = formValues.value?.products?.length
      if (l > 0) {
        const obj = clone(formValues.value.products[l - 1])
        delete obj.width
        delete obj.length
        return obj
      }
      return getEmptyProduct()
    }

    const addProduct = () => formValues.value.products.push(getNewProduct())
    const loading = ref(false)
    const ready = ref(!props.edit_doc_number)

    addProduct()

    const selectedProduct = (product, index) => {
      const obj = Object.assign({}, formValues.value.products[index], product, {
        quantity: 1,
        vat: 21,
      })

      formValues.value.products[index] = obj
    }

    const removeProduct = index => {
      if (index <= -1 || !formValues.value.products.length) return
      formValues.value.products.splice(index, 1)
    }

    const api = useApi()
    const postData = computed(() => {
      const form = formValues.value
      const postData = clone(form)
      postData.client_id = form.client?.id || form.client?.client_id
      postData.products = clone(postData.products)
        .filter(el => {
          if (props.creditNote) {
            return !el.id && el.quantity === 1 && el.price > 0
          }
          const check1Bis = (el.id == 0 || el.code === 'ACOMPTE') && el.quantity > 0
          const check1 = check1Bis || (el.id && el.quantity > 0 && el.price > 0)

          const check2 = el.price_type !== 'PER_SQUARE_METER' || (el.width > 0 && el.length > 0)
          const check3 = el.price_type !== 'PER_METER' || el.length > 0

          return check1 && check2 && check3
        })
        .map(obj => {
          if (obj.price_type === 'FIXED') {
            delete obj.width
            delete obj.length
          } else if (obj.price_type === 'PER_METER') {
            delete obj.width
          }
          return obj
        })

      delete postData.client
      return postData
    })

    const handleSubmitCreation = () => {
      api[props.apiType].create(postData.value).then(({ data }) => {
        // console.log({ data })
        router.push(`/${props.apiType}/${data.doc?.doc_number}/upload`)
      })
    }

    const handleSubmitEdit = () => {
      if (props.edit_doc_number) {
        api[props.apiType].edit(Object.assign({}, postData.value, { doc_number: props.edit_doc_number })).then(() => {
          router.push(`/${props.apiType}/${props.edit_doc_number}/upload`)
        })
      }
    }

    let mockDate = ref(new Date())
    if (props.edit_doc_number) {
      if (props.apiType !== 'quotes' && props.apiType !== 'invoices') {
        alert(`Il y a que les Document 'Devis' et 'Factures' qui sont modifiables.`)
        router.push('/quotes')
        return
      }
      api[props.apiType].get({ doc_number: props.edit_doc_number }).then(res => {
        if (res?.data?.doc) {
          // if (res.data.doc.amount_open > 0 || res.data.doc.doc_number_linked) {
          //   alert(`Devis ${props.edit_doc_number} déjà facturée`)
          //   router.push('/quotes')
          //   return
          // }
          // if (res.data.doc.production_id) {
          //   alert(`Devis ${props.edit_doc_number} déjà en production`)
          //   router.push('/quotes')
          //   return
          // }
        }
        if (res?.data?.doc?.date) {
          mockDate.value = new Date(res.data.doc.date)
        }
        if (res?.data?.client) {
          formValues.value.client = res.data.client
        }
        if (res?.data?.products) {
          formValues.value.products = res.data.products
        }
        if (res?.data?.doc?.description) {
          formValues.value.description = res.data.doc.description
        }
        if (res?.data?.doc?.rules) {
          formValues.value.rules = res.data.doc.rules
        }
        ready.value = true
      })
    }

    const mockDocument = computed(() => ({
      type: props.type,
      date: mockDate.value,
      client: formValues.value.client,
      products: postData.value.products,
      description: formValues.value.description,
    }))

    let renderKey = ref(0)
    watch(
      () => formValues.value,
      () => (renderKey.value = (renderKey.value + 1) % 1000),
    )

    return {
      formValues,
      loading,
      ready,
      renderKey,
      mockDocument,
      postData,
      addProduct,
      selectedProduct,
      getNewProduct,
      getEmptyProduct,
      removeProduct,
      handleSubmit: props.edit_doc_number ? handleSubmitEdit : handleSubmitCreation,
    }
  },
}
</script>

<style scoped>
.invoice-box {
  margin: 0;
}
</style>
